import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { IGameState } from 'State/Game/IGameState';
import { DefaultQuestsPopupLayout } from 'Layouts/Quests/DefaultQuestsPopupLayout';
import { UIActions } from 'State/UI/UIActions';
import Game from 'Game';
import { ArrayHelper } from 'Utility/ArrayHelper';
import QuestItem from 'Prototype/UI/QuestItem';
import QuestHelper from 'Utility/QuestHelper';
import { PopupID } from 'State/UI/PopupID';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { createEventLayout, initButton, setEventQuestProgress, updateEventData } from 'System/UI/Menu/QuestsMenuHelper';
import { getEventLayout } from 'State/Events/EventHelper';
import { EventQuestPopupLayout } from 'Constants/Events/CurrentEvent';

export default class QuestsMenuHandler extends BaseMenu {
    private layout: GLayout<DefaultQuestsPopupLayout> | GLayout<EventQuestPopupLayout>;
    private questItems: QuestItem[] = [];

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.QUEST_MENU, uiSystem, false);
    }

    public onCreate() {
        super.onCreate();

        const state = Game.store.getState();

        const eventsWithQuests = EventStateHelper.getEventsWithQuests(state);
        const validEvent = ArrayHelper.sortByHighestPriority(eventsWithQuests)[0];
        const eventLayout = validEvent ? getEventLayout(validEvent.type, PopupID.QUEST_MENU) : null;

        this.layout = eventLayout ?? getLayout(DefaultQuestsPopupLayout);

        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        initButton(this.layout.subLayout.button, this.close, this.cleanupManager);
        initButton(this.layout.subLayout.closeButton, this.close, this.cleanupManager);

        this.layout.subLayout.quests.forEach((questObject: any, index: number) => {
            const questItem = new QuestItem({
                layout: this.layout.subLayout.quests[index],
                data: state.playerState.quests[index],
                questIndex: index,
            });

            this.questItems.push(questItem);
            this.cleanupManager.add(() => {
                questItem.destroy();
                this.questItems = ArrayHelper.remove(this.questItems, questItem);
            });
        });

        if (eventLayout) {
            createEventLayout(state, this.layout as GLayout<EventQuestPopupLayout>, this.cleanupManager);
        }
    }

    public onShow(params: unknown) {
        const state = Game.store.getState();
        this.questItems.forEach((questItem, index) => {
            questItem.updateData(state.playerState.quests[index], state);
        });

        updateEventData(state, this.layout);
    }

    private readonly close = () => {
        if (this.blockClose) return;
        UIActions.hidePopup(PopupID.QUEST_MENU);
    };

    public async preload() {
        const state = Game.store.getState();
        state.playerState.quests.forEach(quest => {
            if (!QuestHelper.isQuestOnCooldown(quest)) {
                QuestHelper.addTileIfNeeded(quest.type, quest.tier);
            }
        });
        // If there is an active quest then preload the event quest assets
        // TODO: This should be dynamic like the layouts in some way
        const eventQuest = EventStateHelper.getActiveQuest(state);
        if (eventQuest) {
            this.resources.set('event-quest-atlas', 'resource/graphics/sprite_sheets/event_quests.json');
            if (eventQuest.type) {
                const eventPoolKey = QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType);
                QuestHelper.addTileIfNeeded(eventQuest.type, eventQuest.tier, eventPoolKey);
            }
        }

        await super.preload();
    }

    public handleStateChanged(state: IGameState): void {
        super.handleStateChanged(state);
        this.questItems.forEach(questItem => questItem.handleStateChanged(state));

        updateEventData(state, this.layout);
        setEventQuestProgress(state, this.layout);
    }

    public get blockClose(): boolean {
        return Boolean(this.questItems.find((e: QuestItem) => e && e.isAnimating));
    }
}
